/**
 * Calendar Editor
 * Authenticated CRUD interface for public.calendar rows.
 * Auth: magic-link email → session token stored in cookie.
 * PDFs: blob wins over URL when both are present.
 */

import { useState, useEffect, useRef, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';

const API = '/api/calendar-editor';

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------
const setCookie = (name, value, days = 90) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
};
const getCookie = (name) => {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};
const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

// ---------------------------------------------------------------------------
// Blank form template  (no 'published' – that's the entry secretary's domain)
// ---------------------------------------------------------------------------
const BLANK = {
  CalendarID: '',
  Hill: '',
  Date: '',
  eventname: '',
  secretary: '',
  secretary_email: '',
  subclass_fe_email: '',
  subclass_vc_email: '',
  subclass_rl_email: '',
  membership_sec_email: '',
  finance_sec_email: '',
  open: false,
  roadlegal: false,
  forgotten_era: false,
  vc: false,
  restricted: false,
  giag: false,
  external_entries: false,
  under16: '',
  regsdate: '',
  closingdate: '',
  notes: '',
  weather: '',
  track_condition: '',
  regs_url: '',
  entry_form_url: '',
  results_url: '',
  first_bike_price: '',
  second_bike_price: '',
  first_3w_price: '',
  second_3w_price: '',
  giag_price: '',
  acu_permit: '',
  course_licence_no: '',
  timing_system: '',
  timing_url: '',
};

// ---------------------------------------------------------------------------
// Live timing system options + per-system URL hint text
// ---------------------------------------------------------------------------
const TIMING_SYSTEMS = [
  { value: '', label: '— not set —' },
  { value: 'swt', label: 'SWT (timesonline.org.uk)' },
  { value: 'swtlive', label: 'SWT Live (swtimekeepinglive.co.uk)' },
  { value: 'tsl', label: 'TSL email ingest (Gurston Down)' },
  { value: 'tsllive', label: 'TSL Live SignalR (Gurston Down)' },
  { value: 'timeteam', label: 'TimeTeam Live Timing (Harewood)' },
  { value: 'resultsman', label: 'Resultsman (Shelsley Walsh / Prescott / Loton Park)' },
  { value: 'fds', label: 'FDS Timing (Oliver’s Mount / Scarborough)' },
  { value: 'nigan', label: 'NIGAN (NHCA’s own timing — Curborough / Hartland / Wiscombe)' },
];

const TIMING_URL_HINTS = {
  swt: 'Base URL for the event, no filename, e.g. https://www.timesonline.org.uk/nhca/2026/r3/',
  swtlive: 'Direct results page URL, e.g. https://www.swtimekeepinglive.co.uk/results.php?event=123',
  tsl: 'Sender email address or domain to filter on, e.g. tsl-timing.com or PCSet3@tsl-timing.com',
  tsllive: 'Event page URL, e.g. http://lt2.tsl-timing.com/262884',
  timeteam: 'Site root, no trailing slash, e.g. https://www.timeteamlivetiming.co.uk',
  resultsman: 'EventViewer.php link from the venue, e.g. http://82.165.193.238/LiveTiming/EventViewer.php?mmtok=...&DB=Resultsman&Download=2202',
  fds: 'webresults.fdstiming.com course link from 243 RRA, e.g. https://webresults.fdstiming.com/course/2239_qTcKzFDU — no entry list needed ahead of time, class comes from their own category text where NHCA doesn’t already have one',
  nigan: 'Leave blank — NIGAN publishes live timing itself; the 19:00 results CSV is read from MQTT',
};

// Default timing_system per hill (case-insensitive substring match on Hill name).
// Only pre-selects the dropdown for new events — the secretary can still change it.
const HILL_TIMING_DEFAULTS = [
  ['shelsley', 'resultsman'],
  ['loton', 'resultsman'],
  ['prescott', 'resultsman'],
  ['tregrehan', 'swt'],
  ['wiscombe', 'swt'],
  ['gurston', 'tsllive'],
  ['harewood', 'timeteam'],
  ['scarborough', 'fds'],
  ['curborough', 'nigan'],  // also matches Curborough X
  ['hartland', 'nigan'],    // every Hartland variant (Hartland B etc.)
];

function defaultTimingSystemForHill(hill) {
  if (!hill) return '';
  const lower = hill.toLowerCase();
  const match = HILL_TIMING_DEFAULTS.find(([key]) => lower.includes(key));
  return match ? match[1] : '';
}

function eventToForm(ev) {
  const d = (v) => (v ? v.substring(0, 10) : '');
  return {
    CalendarID: ev.CalendarID ?? '',
    Hill: ev.Hill ?? '',
    Date: d(ev.Date),
    eventname: ev.eventname ?? '',
    secretary: ev.secretary ?? '',
    secretary_email: ev.secretary_email ?? '',
    subclass_fe_email: ev.subclass_fe_email ?? '',
    subclass_vc_email: ev.subclass_vc_email ?? '',
    subclass_rl_email: ev.subclass_rl_email ?? '',
    membership_sec_email: ev.membership_sec_email ?? '',
    finance_sec_email: ev.finance_sec_email ?? '',
    open: !!ev.open,
    roadlegal: !!ev.roadlegal,
    forgotten_era: !!ev.forgotten_era,
    vc: !!ev.vc,
    restricted: !!ev.restricted,
    giag: !!ev.giag,
    external_entries: !!ev.external_entries,
    under16: ev.under16 ?? '',
    regsdate: d(ev.regsdate),
    closingdate: d(ev.closingdate),
    notes: ev.notes ?? '',
    weather: ev.weather ?? '',
    track_condition: ev.track_condition ?? '',
    regs_url: ev.regs_url ?? '',
    entry_form_url: ev.entry_form_url ?? '',
    results_url: ev.results_url ?? '',
    first_bike_price: ev.first_bike_price ?? '',
    second_bike_price: ev.second_bike_price ?? '',
    first_3w_price: ev.first_3w_price ?? '',
    second_3w_price: ev.second_3w_price ?? '',
    giag_price: ev.giag_price ?? '',
    acu_permit: ev.acu_permit ?? '',
    course_licence_no: ev.course_licence_no ?? '',
    timing_system: ev.timing_system ?? '',
    timing_url: ev.timing_url ?? '',
  };
}

function autoEventName(hill, date) {
  if (!hill || !date) return '';
  const d = new Date(date + 'T12:00:00');
  const formatted = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${hill.trim()} - ${formatted}`;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const S = {
  page: { minHeight: '100vh', background: '#f0f2f5', fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" },
  wrap: { maxWidth: 1600, margin: '0 auto', padding: '0 1.5rem 2rem' },
  header: { background: 'linear-gradient(135deg,#1b3d6d,#0f2847)', color: 'white', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: 0 },
  headerTitle: { margin: 0, fontSize: '1.3rem', fontWeight: 700 },
  headerMeta: { fontSize: '0.85rem', opacity: 0.8 },
  btn: { padding: '0.5rem 1.1rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' },
  btnPrimary: { background: '#fb6107', color: 'white' },
  btnSecondary: { background: '#e0e4ea', color: '#333' },
  btnDanger: { background: '#e74c3c', color: 'white' },
  btnSmall: { padding: '0.3rem 0.7rem', fontSize: '0.8rem', borderRadius: 5, border: 'none', cursor: 'pointer', fontWeight: 600 },
  card: { background: 'white', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem 1.5rem', marginTop: '1rem' },
  label: { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#555', marginBottom: 3, marginTop: 10 },
  input: { width: '100%', padding: '0.42rem 0.6rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9rem', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '0.42rem 0.6rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9rem', minHeight: 60, boxSizing: 'border-box', resize: 'vertical' },
  error: { background: '#fdecea', color: '#c0392b', padding: '0.75rem 1rem', borderRadius: 8, margin: '0.5rem 0', fontSize: '0.9rem' },
  hillWarning: { background: '#fef5e7', color: '#9a5b00', border: '1px solid #f5c26b', padding: '0.5rem 0.7rem', borderRadius: 6, marginTop: 6, fontSize: '0.82rem', lineHeight: 1.4 },
  hillSuggestion: { background: 'none', border: 'none', padding: 0, color: '#1b3d6d', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' },
  success: { background: '#eafaf1', color: '#1e8449', padding: '0.75rem 1rem', borderRadius: 8, margin: '0.5rem 0', fontSize: '0.9rem' },
  sectionHead: { fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#999', marginTop: 18, marginBottom: 2, borderBottom: '1px solid #f0f0f0', paddingBottom: 3 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '2rem 1rem' },
  modal: { background: 'white', borderRadius: 12, width: '100%', maxWidth: 900, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', padding: '1.75rem' },
  modalTitle: { margin: '0 0 0.75rem', fontSize: '1.2rem', fontWeight: 700 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, paddingTop: 14, borderTop: '1px solid #eee' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' },
  th: { textAlign: 'left', padding: '0.6rem 0.75rem', background: '#f5f7fa', borderBottom: '2px solid #e0e4ea', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '0.5rem 0.75rem', borderBottom: '1px solid #eef0f3', verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '0.15rem 0.45rem', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600, marginRight: 3 },
  pdfRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.25rem' },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 1.25rem' },
  grid4: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 1.25rem' },
  optionalTag: { fontSize: '0.72rem', fontWeight: 500, color: '#aaa' },
  checklistRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', padding: '3px 0' },
};

// ---------------------------------------------------------------------------
// Entry List readiness
// The online entry form (/enter/:calendarId) shouldn't be handed to riders
// until these fields are set. "regs" means either a URL or an uploaded PDF.
// Restricted to 2027+ events — the online entry form is a 2027-season
// rollout, so 2026-and-earlier events must not be touched by any of this.
// ---------------------------------------------------------------------------
const ONLINE_ENTRY_FIRST_YEAR = 2027;

// A 2027+ GIAG event needs its GIAG price before online entry opens. Like
// the other fees it may not be known yet, so the event saves without it.
function giagPriceMissing(form) {
  return !!form.giag && usesOnlineEntry(form) && String(form.giag_price ?? '').trim() === '';
}

function isOnlineEntryEra(dateStr) {
  return Number(dateStr?.substring(0, 4)) >= ONLINE_ENTRY_FIRST_YEAR;
}

// 2027+ and not an event whose entries the organiser handles themselves
function usesOnlineEntry(ev) {
  return isOnlineEntryEra(ev.Date) && !ev.external_entries;
}

const ENTRY_LIST_REQUIRED_FIELDS = [
  { key: 'first_bike_price', label: 'First Bike entry fee' },
  { key: 'first_3w_price', label: 'First 3-Wheeler entry fee' },
  { key: 'regs', label: 'Regulations (URL or upload)' },
  { key: 'acu_permit', label: 'Permit No.' },
  { key: 'course_licence_no', label: 'Course Lic/Cert No.' },
  { key: 'secretary_email', label: 'Main Secretary email' },
  { key: 'finance_sec_email', label: 'Finance Secretary email' },
];

function isFieldMissing(ev, key) {
  if (key === 'regs') return !(ev.has_regs_pdf || (ev.regs_url || '').trim());
  const v = ev[key];
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

function missingEntryListFields(ev) {
  const missing = ENTRY_LIST_REQUIRED_FIELDS.filter(({ key }) => isFieldMissing(ev, key));
  if (giagPriceMissing(ev)) missing.push({ key: 'giag_price', label: 'Give It A Go price' });
  return missing;
}

function onlineEntryUrl(calendarId) {
  return `https://query.nhca.co.uk/enter/${calendarId}`;
}

// ---------------------------------------------------------------------------
// Email validation — basic format check plus a domain-typo nudge.
// Never blocks saving on a typo suggestion (a real but uncommon domain can
// coincidentally look like a near-miss); only a genuinely malformed address
// blocks save, same "flag, don't block" pattern as checkSecretaryName.
// ---------------------------------------------------------------------------
const COMMON_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk', 'outlook.com',
  'yahoo.com', 'yahoo.co.uk', 'icloud.com', 'live.com', 'live.co.uk',
  'btinternet.com', 'sky.com', 'talktalk.net', 'aol.com', 'ntlworld.com',
  'virginmedia.com', 'blueyonder.co.uk',
];

// Edit distance allowing adjacent-character transposition as a single edit
// (Damerau-Levenshtein) — catches the most common typing slips, e.g.
// "gmial.com" vs "gmail.com", without matching unrelated short domains.
function editDistance(a, b) {
  const m = a.length, n = b.length;
  const d = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost);
      }
    }
  }
  return d[m][n];
}

// Returns '' (fine), a blocking format message, or a non-blocking "Did you
// mean…" suggestion. Blank values are always fine — these fields are optional.
function checkEmailField(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1 || trimmed.slice(at + 1).indexOf('.') === -1) {
    return "This doesn't look like a valid email address.";
  }
  const domain = trimmed.slice(at + 1).toLowerCase();
  if (COMMON_EMAIL_DOMAINS.includes(domain)) return '';
  let best = null;
  for (const candidate of COMMON_EMAIL_DOMAINS) {
    const dist = editDistance(domain, candidate);
    if (dist > 0 && dist <= 2 && (!best || dist < best.dist)) best = { candidate, dist };
  }
  return best ? `Did you mean ${trimmed.slice(0, at + 1)}${best.candidate}?` : '';
}

function isBlockingEmailError(msg) {
  return !!msg && !msg.startsWith('Did you mean');
}

// ---------------------------------------------------------------------------
// Module-level form field components
// Defined HERE (outside all other components) so React never sees them as
// new types on re-render — fixing the "one keystroke then stops" bug.
// ---------------------------------------------------------------------------

function FormField({ label, field, type, placeholder, form, onSet, readOnly, onBlur, error, datalistId, datalistOptions }) {
  // Datalist suggestions are filtered by the browser against the input's
  // actual value, not its text selection — select()-ing the existing text
  // on focus doesn't change what's filtered. To show the full suggestion
  // list on focus we clear the value outright, then restore it on blur if
  // nothing was picked/typed in the meantime. Typing and then deleting
  // everything leaves it blank, as does the Clear button.
  const [preFocusValue, setPreFocusValue] = useState(null);
  const editedRef = useRef(false);
  const value = form[field] ?? '';
  const canClear = datalistId && !readOnly && (value || preFocusValue);
  return (
    <div>
      <label style={{ ...S.label, display: 'flex', justifyContent: 'space-between' }}>
        <span>{label}</span>
        {canClear && (
          <button type="button" title="Remove this address"
            style={{ border: 'none', background: 'none', padding: 0, color: '#c0392b', cursor: 'pointer', fontSize: '0.75rem' }}
            // Keep focus in the box so its blur doesn't put the old value back first
            onMouseDown={e => e.preventDefault()}
            onClick={() => { setPreFocusValue(null); editedRef.current = true; onSet(field, ''); }}>
            Clear
          </button>
        )}
      </label>
      <input
        style={{ ...S.input, background: readOnly ? '#f7f7f7' : undefined, color: readOnly ? '#888' : undefined,
                 borderColor: error ? '#c0392b' : undefined }}
        type={type || 'text'}
        value={value}
        placeholder={placeholder || ''}
        readOnly={!!readOnly}
        list={datalistId}
        autoComplete={datalistId ? 'off' : undefined}
        onChange={e => { if (readOnly) return; editedRef.current = true; onSet(field, e.target.value); }}
        onFocus={datalistId && !readOnly ? () => {
          editedRef.current = false;
          setPreFocusValue(value);
          if (value) onSet(field, '');
        } : undefined}
        onBlur={() => {
          if (datalistId && !value && preFocusValue && !editedRef.current) {
            onSet(field, preFocusValue);
          }
          setPreFocusValue(null);
          if (onBlur) onBlur(value);
        }}
      />
      {error && <div style={{ color: '#c0392b', fontSize: '0.78rem', marginTop: 3 }}>{error}</div>}
      {datalistId && (
        <datalist id={datalistId}>
          {(datalistOptions || []).map(v => <option key={v} value={v} />)}
        </datalist>
      )}
    </div>
  );
}

const CANCELLED_HILL_RE = /cancelled|abandoned/i;

function isNewHillName(name, hills) {
  const typed = (name ?? '').trim();
  return hills.length > 0 && typed !== '' && !CANCELLED_HILL_RE.test(typed) && !hills.includes(typed);
}

// Live timing, entry lists, results and the results hub all match an event
// by its exact hill name, so a name that isn't an established one (see
// list_hills in calendar_editor_routes.py) gets a warning — not a block, a
// genuinely new course is fine. Existing names sharing the first word are
// offered as one-click replacements (e.g. "Hartland Quay" -> Hartland B/C/D).
function HillField({ form, onSet, hills }) {
  const selectableHills = hills.filter(h => !CANCELLED_HILL_RE.test(h));
  const typed = (form.Hill ?? '').trim();
  const isNewName = isNewHillName(typed, hills);
  const firstWord = typed.split(/\s+/)[0].toLowerCase();
  const similar = isNewName
    ? selectableHills.filter(h => h.toLowerCase() === typed.toLowerCase() || h.split(/\s+/)[0].toLowerCase() === firstWord)
    : [];
  return (
    <div>
      <label style={S.label}>Hill *</label>
      <input
        style={S.input}
        type="text"
        value={form.Hill ?? ''}
        list="hills-datalist"
        autoComplete="off"
        onChange={e => onSet('Hill', e.target.value)}
      />
      <datalist id="hills-datalist">
        {selectableHills.map(h => <option key={h} value={h} />)}
      </datalist>
      {isNewName && (
        <div style={S.hillWarning}>
          ⚠ New hill name. Live timing, entry lists and results match on the exact name,
          so use an existing one unless this is a genuinely new course.
          {similar.length > 0 && (
            <div style={{ marginTop: 4 }}>
              Did you mean{' '}
              {similar.map((h, i) => (
                <Fragment key={h}>
                  {i > 0 && ', '}
                  <button type="button" style={S.hillSuggestion} onClick={() => onSet('Hill', h)}>{h}</button>
                </Fragment>
              ))}?
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CheckField({ label, field, form, onSet }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.88rem', marginTop: 8, userSelect: 'none' }}>
      <input type="checkbox" checked={!!form[field]} onChange={e => onSet(field, e.target.checked)} />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Login screen
// ---------------------------------------------------------------------------
function LoginScreen() {
  const [email, setEmail] = useState(() => getCookie('cal_editor_saved_email') || '');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setCookie('cal_editor_saved_email', email.trim().toLowerCase(), 365);
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) setError((await res.json()).error || 'Request failed');
      else setSent(true);
    } catch { setError('Network error – please try again'); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', padding: '2rem', width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem' }}>📅</div>
          <h1 style={{ margin: '0.5rem 0 0.25rem', fontSize: '1.5rem', fontWeight: 700, color: '#1b3d6d' }}>NHCA Calendar Editor</h1>
          <p style={{ color: '#888', margin: 0, fontSize: '0.9rem' }}>National Hillclimb Association</p>
        </div>
        {sent ? (
          <div style={S.success}>Check your email for a login link. Valid for 24 hours, single use.</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div style={S.error}>{error}</div>}
            <label style={S.label}>Email address</label>
            <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com" required autoFocus />
            <button style={{ ...S.btn, ...S.btnPrimary, width: '100%', marginTop: 16, padding: '0.75rem' }}
              type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send Login Link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF upload/status sub-component
// ---------------------------------------------------------------------------
function PdfField({ calendarId, pdfType, label, hasBlob, url, sessionToken, onChanged, onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef();

  const pdfUrl = hasBlob ? `${API}/events/${calendarId}/pdf/${pdfType}` : url || null;
  const source = hasBlob ? 'stored PDF' : url ? 'URL' : null;

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setMsg('');
    const fd = new FormData();
    fd.append('pdf', file);
    try {
      const res = await fetch(`${API}/events/${calendarId}/pdf/${pdfType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}` },
        body: fd,
      });
      const j = await res.json();
      if (!res.ok) setMsg(j.error || 'Upload failed');
      else { setMsg('Uploaded'); onChanged(); onUploadSuccess?.(); }
    } catch { setMsg('Network error'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  async function handleDelete() {
    if (!window.confirm(`Remove the stored ${label} PDF? The URL (if set) will be used as fallback.`)) return;
    setMsg('');
    try {
      const res = await fetch(`${API}/events/${calendarId}/pdf/${pdfType}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const j = await res.json();
      if (!res.ok) setMsg(j.error || 'Delete failed');
      else { setMsg('Removed'); onChanged(); }
    } catch { setMsg('Network error'); }
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div style={S.pdfRow}>
        {pdfUrl ? (
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '0.82rem', color: '#1b3d6d', fontWeight: 600 }}>
            View {label} ({source})
          </a>
        ) : (
          <span style={{ fontSize: '0.82rem', color: '#aaa' }}>No {label} stored</span>
        )}
        {hasBlob && (
          <button style={{ ...S.btnSmall, background: '#e74c3c', color: 'white' }} onClick={handleDelete}>
            Remove PDF
          </button>
        )}
        <label style={{ ...S.btnSmall, background: '#e0e4ea', color: '#333', cursor: 'pointer', margin: 0 }}>
          {uploading ? 'Uploading…' : hasBlob ? 'Replace PDF' : 'Upload PDF'}
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
            onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
      {msg && <div style={{ fontSize: '0.8rem', color: msg.startsWith('Upload') || msg === 'Removed' ? '#27ae60' : '#c0392b', marginTop: 3 }}>{msg}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entry List readiness dialog — shown right after regulations are provided
// (PDF upload or URL entered), prompting for the other fields the online
// entry form needs before the Entry List link can go live.
// ---------------------------------------------------------------------------
function ReadinessDialog({ event, form, sessionToken, secretaryEmails = {}, onSaved, onCancel }) {
  const [fields, setFields] = useState({
    first_bike_price: form.first_bike_price ?? '',
    second_bike_price: form.second_bike_price ?? '',
    first_3w_price: form.first_3w_price ?? '',
    second_3w_price: form.second_3w_price ?? '',
    acu_permit: form.acu_permit ?? '',
    course_licence_no: form.course_licence_no ?? '',
    secretary_email: form.secretary_email ?? '',
    finance_sec_email: form.finance_sec_email ?? '',
    giag_price: form.giag_price ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [emailErrors, setEmailErrors] = useState({});

  function set(field, value) {
    setFields(f => ({ ...f, [field]: value }));
  }

  function checkEmail(field, value) {
    setEmailErrors(e => ({ ...e, [field]: checkEmailField(value) }));
  }

  const stillMissing = missingEntryListFields({ ...form, ...fields }).filter(m => m.key !== 'regs');

  async function handleSave() {
    setError('');
    if (Object.values(emailErrors).some(isBlockingEmailError)) return;
    setSaving(true);
    try {
      const payload = { ...form, ...fields };
      const res = await fetch(`${API}/events/${event.CalendarID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (!res.ok) setError(j.error || 'Save failed');
      else onSaved(j, fields);
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  }

  function handleCancel() {
    const ok = window.confirm(
      'The Entry List link will not be available to riders until all required fields are set. ' +
      'Cancel anyway?'
    );
    if (ok) onCancel();
  }

  return (
    <div style={{ ...S.overlay, zIndex: 1100 }}>
      <div style={{ ...S.modal, maxWidth: 560 }}>
        <h2 style={S.modalTitle}>Complete Entry List details</h2>
        <p style={{ fontSize: '0.88rem', color: '#666', marginTop: 0 }}>
          These are required before the Entry List link (/enter/{event.CalendarID}) can be used. You can cancel — this will ask again next time you edit the event.
        </p>
        {error && <div style={S.error}>{error}</div>}

        <div style={S.sectionHead}>Required</div>
        <div style={S.grid2}>
          <FormField label="First Bike entry fee (£)" field="first_bike_price" type="number" placeholder="0.00" form={fields} onSet={set} />
          <FormField label="First 3-Wheeler entry fee (£)" field="first_3w_price" type="number" placeholder="0.00" form={fields} onSet={set} />
        </div>
        <div style={S.grid2}>
          <FormField label="Permit No." field="acu_permit" placeholder="e.g. ACU/12345" form={fields} onSet={set} />
          <FormField label="Course Lic/Cert No." field="course_licence_no" placeholder="e.g. CL-2026-001" form={fields} onSet={set} />
        </div>
        <div style={S.grid2}>
          <FormField label="Main Secretary email" field="secretary_email" type="email" form={fields} onSet={set}
            onBlur={v => checkEmail('secretary_email', v)} error={emailErrors.secretary_email}
            datalistId="readiness-secretary-email-datalist" datalistOptions={secretaryEmails.secretary_email} />
          <FormField label="Finance Secretary email" field="finance_sec_email" type="email" form={fields} onSet={set}
            onBlur={v => checkEmail('finance_sec_email', v)} error={emailErrors.finance_sec_email}
            datalistId="readiness-finance-sec-email-datalist" datalistOptions={secretaryEmails.finance_sec_email} />
        </div>
        {form.giag && (
          <div style={S.grid2}>
            <FormField label="Give It A Go price, all-in (£)" field="giag_price" type="number" placeholder="0.00" form={fields} onSet={set} />
          </div>
        )}

        <div style={S.sectionHead}>
          Second machine fees <span style={S.optionalTag}>(optional)</span>
        </div>
        <div style={S.grid2}>
          <FormField label="Second Bike entry fee (£)" field="second_bike_price" type="number" placeholder="0.00" form={fields} onSet={set} />
          <FormField label="Second 3-Wheeler entry fee (£)" field="second_3w_price" type="number" placeholder="0.00" form={fields} onSet={set} />
        </div>

        <div style={S.sectionHead}>Entry List status</div>
        {stillMissing.length === 0 ? (
          <div style={{ ...S.checklistRow, color: '#1e8449' }}>✓ All required fields set — Entry List link will be enabled.</div>
        ) : (
          stillMissing.map(m => (
            <div key={m.key} style={{ ...S.checklistRow, color: '#c0392b' }}>✗ {m.label}</div>
          ))
        )}

        <div style={S.modalFooter}>
          <button style={{ ...S.btn, ...S.btnSecondary }} onClick={handleCancel} disabled={saving}>Cancel</button>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit modal
// ---------------------------------------------------------------------------
function EventModal({ event, sessionToken, hills, secretaryEmails = {}, onSave, onClose, onQuietUpdate, defaultYear }) {
  const isNew = !event;
  const [form, setForm] = useState(isNew
    ? { ...BLANK, Date: defaultYear ? `${defaultYear}-01-01` : '' }
    : eventToForm(event));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [secretaryNameError, setSecretaryNameError] = useState('');
  const [emailErrors, setEmailErrors] = useState({});
  const [pdfState, setPdfState] = useState({
    has_regs_pdf: event?.has_regs_pdf,
    has_entry_form_pdf: event?.has_entry_form_pdf,
  });
  const [showReadinessDialog, setShowReadinessDialog] = useState(false);
  const regsPromptShownRef = useRef(false);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function checkEmail(field, value) {
    setEmailErrors(e => ({ ...e, [field]: checkEmailField(value) }));
  }

  // "Secretary Name" is a plain display name (shown on the public calendar
  // listing) — easy to confuse with the "Main Secretary" email field further
  // down. Catch an accidentally-pasted email address on blur rather than
  // waiting until save.
  function checkSecretaryName(value) {
    setSecretaryNameError(
      value.includes('@')
        ? 'This should be a name, not an email address — use the Main Secretary field below for that.'
        : ''
    );
  }

  // Prompt for the other Entry List fields as soon as regs are provided
  // (PDF upload or URL typed in) — but only once per time the modal is open,
  // and only if something is still missing.
  function maybeTriggerRegsPrompt(latestForm = form, latestPdfState = pdfState) {
    if (isNew || regsPromptShownRef.current || !usesOnlineEntry(latestForm)) return;
    const hasRegs = latestPdfState.has_regs_pdf || (latestForm.regs_url || '').trim();
    if (!hasRegs) return;
    const stillMissing = missingEntryListFields({ ...latestForm, ...latestPdfState }).filter(m => m.key !== 'regs');
    if (stillMissing.length === 0) return;
    regsPromptShownRef.current = true;
    setShowReadinessDialog(true);
  }

  // Auto-generate event name from hill + date (new events only)
  useEffect(() => {
    if (!isNew) return;
    const name = autoEventName(form.Hill, form.Date);
    if (name) set('eventname', name);
  }, [form.Hill, form.Date]);

  // Pre-select the timing system based on the hill (new events only; still editable)
  useEffect(() => {
    if (!isNew) return;
    const def = defaultTimingSystemForHill(form.Hill);
    if (def) set('timing_system', def);
  }, [form.Hill]);

  // Re-open the readiness dialog on mount if regs are already present but
  // other Entry List fields are still missing — this is what makes "cancel
  // and come back to this later" true: re-opening Edit asks again.
  useEffect(() => {
    maybeTriggerRegsPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshPdfState() {
    try {
      const res = await fetch(`${API}/events?year=${form.Date?.substring(0, 4) || new Date().getFullYear()}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } });
      if (res.ok) {
        const events = await res.json();
        const updated = events.find(e => e.CalendarID === event?.CalendarID);
        if (updated) {
          const next = { has_regs_pdf: updated.has_regs_pdf, has_entry_form_pdf: updated.has_entry_form_pdf };
          setPdfState(next);
          onQuietUpdate?.(updated);
          return next;
        }
      }
    } catch { /* ignore */ }
    return pdfState;
  }

  async function handleRegsUploaded() {
    const next = await refreshPdfState();
    maybeTriggerRegsPrompt(form, next);
  }

  async function handleSave() {
    setError('');
    if (secretaryNameError) return; // already flagged inline on the field itself
    if (Object.values(emailErrors).some(isBlockingEmailError)) return;
    // Backstop for the inline new-hill warning, in case it was skipped past.
    // Only when the hill is being set or changed, so re-saving an event that
    // already has a (confirmed) new name doesn't ask every time.
    const hillChanged = isNew || (form.Hill ?? '').trim() !== (event.Hill ?? '').trim();
    if (hillChanged && isNewHillName(form.Hill, hills) && !window.confirm(
      `"${form.Hill.trim()}" isn't a hill name used before. Live timing, entry lists and results ` +
      `match on the exact name, so an existing course should use its existing name.\n\n` +
      `Are you sure this is a new hill?`
    )) return;
    setSaving(true);
    try {
      const url = isNew ? `${API}/events` : `${API}/events/${event.CalendarID}`;
      const method = isNew ? 'POST' : 'PUT';
      const payload = { ...form };
      if (isNew && !payload.CalendarID) delete payload.CalendarID;

      // Existing events: the CalendarID is already known, so fill the Entry
      // Form URL with the online entry form link in the same request if
      // nothing else has been set for it. 2027+ events only.
      if (!isNew && usesOnlineEntry(payload) && !payload.entry_form_url?.trim() && !pdfState.has_entry_form_pdf) {
        payload.entry_form_url = onlineEntryUrl(event.CalendarID);
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
        body: JSON.stringify(payload),
      });
      let j = await res.json();
      if (!res.ok) { setError(j.error || 'Save failed'); return; }

      // New events: the CalendarID is only known after creation, so a quick
      // follow-up update is needed to fill the Entry Form URL. 2027+ events only.
      if (isNew && usesOnlineEntry(form) && !j.entry_form_url && !j.has_entry_form_pdf) {
        const followUp = await fetch(`${API}/events/${j.CalendarID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
          body: JSON.stringify({ ...form, entry_form_url: onlineEntryUrl(j.CalendarID) }),
        });
        if (followUp.ok) j = await followUp.json();
      }

      onSave(j);
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.overlay} onClick={e => !showReadinessDialog && e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <h2 style={S.modalTitle}>
          {isNew ? 'New Event' : `Edit — ${event.Hill} (${event.Date?.substring(0, 10)})`}
        </h2>
        {error && <div style={S.error}>{error}</div>}

        {/* Basic info */}
        <div style={S.sectionHead}>Basic Info</div>
        <div style={S.grid3}>
          <HillField form={form} onSet={set} hills={hills} />
          <FormField label="Date *" field="Date" type="date" form={form} onSet={set} />
        </div>
        <div style={S.grid2}>
          <FormField label="Event Name (auto-generated; edit if needed)" field="eventname" form={form} onSet={set} />
          <FormField label="Secretary Name (public display only)" field="secretary" form={form} onSet={set}
                     onBlur={checkSecretaryName} error={secretaryNameError}
                     datalistId="secretary-name-datalist" datalistOptions={secretaryEmails.secretary} />
        </div>
        <div style={S.grid2}>
          <FormField label="ACU Permit No." field="acu_permit" placeholder="e.g. ACU/12345" form={form} onSet={set} />
          <FormField label="Course Lic/Cert No." field="course_licence_no" placeholder="e.g. CL-2026-001" form={form} onSet={set} />
        </div>
        <div>
          <label style={S.label}>Notes</label>
          <textarea style={S.textarea} value={form.notes ?? ''} onChange={e => set('notes', e.target.value)} />
        </div>
        <div style={S.grid2}>
          <FormField label="Weather" field="weather" placeholder="e.g. Sunny intervals" form={form} onSet={set} />
          <FormField label="Track Condition" field="track_condition" placeholder="e.g. Dry, warm and grippy" form={form} onSet={set} />
        </div>

        {/* Key dates */}
        <div style={S.sectionHead}>Key Dates</div>
        <div style={S.grid2}>
          <FormField label="Regs Open Date" field="regsdate" type="date" form={form} onSet={set} />
          <FormField label="Entry Closing Date" field="closingdate" type="date" form={form} onSet={set} />
        </div>

        {/* Classes */}
        <div style={S.sectionHead}>Classes Offered</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 2.5rem' }}>
          <CheckField label="Open" field="open" form={form} onSet={set} />
          <CheckField label="Road Legal" field="roadlegal" form={form} onSet={set} />
          <CheckField label="Forgotten Era" field="forgotten_era" form={form} onSet={set} />
          <CheckField label="Vintage / Classic" field="vc" form={form} onSet={set} />
        </div>

        {/* Event flags */}
        <div style={S.sectionHead}>Event Flags</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 2.5rem' }}>
          <CheckField label="Restricted" field="restricted" form={form} onSet={set} />
          <CheckField label="GIAG" field="giag" form={form} onSet={set} />
          <CheckField label="Entries handled by the organiser (not through this website)" field="external_entries" form={form} onSet={set} />
          <div>
            <label style={S.label}>Under 16</label>
            <select style={{ ...S.input, width: 'auto' }} value={form.under16 ?? ''}
              onChange={e => set('under16', e.target.value)}>
              <option value="">— not set —</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Ask">Ask</option>
            </select>
          </div>
        </div>

        {/* Secretary emails */}
        <div style={S.sectionHead}>Secretary Emails</div>
        <div style={S.grid3}>
          <FormField label="Main Secretary" field="secretary_email" type="email" form={form} onSet={set}
            onBlur={v => checkEmail('secretary_email', v)} error={emailErrors.secretary_email}
            datalistId="secretary-email-datalist" datalistOptions={secretaryEmails.secretary_email} />
          <FormField label="Membership Secretary" field="membership_sec_email" type="email" form={form} onSet={set}
            onBlur={v => checkEmail('membership_sec_email', v)} error={emailErrors.membership_sec_email}
            datalistId="membership-sec-email-datalist" datalistOptions={secretaryEmails.membership_sec_email} />
          <FormField label="Finance Secretary" field="finance_sec_email" type="email" form={form} onSet={set}
            onBlur={v => checkEmail('finance_sec_email', v)} error={emailErrors.finance_sec_email}
            datalistId="finance-sec-email-datalist" datalistOptions={secretaryEmails.finance_sec_email} />
          {form.forgotten_era ? (
            <FormField label="FE Sub-class Secretary" field="subclass_fe_email" type="email" form={form} onSet={set}
              onBlur={v => checkEmail('subclass_fe_email', v)} error={emailErrors.subclass_fe_email}
              datalistId="subclass-fe-email-datalist" datalistOptions={secretaryEmails.subclass_fe_email} />
          ) : (
            // Not saved unless the class is offered (the backend drops it too);
            // the address stays in the form in case the box is ticked again
            <FormField label="FE Sub-class Secretary" field="subclass_fe_email" type="email" form={{}} onSet={set}
              readOnly placeholder="Tick Forgotten Era to set this" />
          )}
          {form.vc ? (
            <FormField label="VC Sub-class Secretary" field="subclass_vc_email" type="email" form={form} onSet={set}
              onBlur={v => checkEmail('subclass_vc_email', v)} error={emailErrors.subclass_vc_email}
              datalistId="subclass-vc-email-datalist" datalistOptions={secretaryEmails.subclass_vc_email} />
          ) : (
            // Not saved unless the class is offered (the backend drops it too);
            // the address stays in the form in case the box is ticked again
            <FormField label="VC Sub-class Secretary" field="subclass_vc_email" type="email" form={{}} onSet={set}
              readOnly placeholder="Tick Vintage / Classic to set this" />
          )}
          {form.roadlegal ? (
            <FormField label="RL Sub-class Secretary" field="subclass_rl_email" type="email" form={form} onSet={set}
              onBlur={v => checkEmail('subclass_rl_email', v)} error={emailErrors.subclass_rl_email}
              datalistId="subclass-rl-email-datalist" datalistOptions={secretaryEmails.subclass_rl_email} />
          ) : (
            // Not saved unless the class is offered (the backend drops it too);
            // the address stays in the form in case the box is ticked again
            <FormField label="RL Sub-class Secretary" field="subclass_rl_email" type="email" form={{}} onSet={set}
              readOnly placeholder="Tick Road Legal to set this" />
          )}
        </div>

        {/* Pricing */}
        <div style={S.sectionHead}>Pricing (£)</div>
        <div style={S.grid4}>
          <FormField label="First Bike" field="first_bike_price" type="number" placeholder="0.00" form={form} onSet={set} />
          <FormField label="Second Bike" field="second_bike_price" type="number" placeholder="0.00" form={form} onSet={set} />
          <FormField label="First 3-Wheeler" field="first_3w_price" type="number" placeholder="0.00" form={form} onSet={set} />
          <FormField label="Second 3-Wheeler" field="second_3w_price" type="number" placeholder="0.00" form={form} onSet={set} />
        </div>
        {form.giag && (
          <div style={S.grid4}>
            <FormField label="Give It A Go (all-in)" field="giag_price" type="number" placeholder="0.00" form={form} onSet={set}
              error={giagPriceMissing(form) ? 'Needed before online entry opens' : ''} />
            <div style={{ gridColumn: 'span 3', alignSelf: 'center', fontSize: '0.8rem', color: '#777' }}>
              One price for a first-timer: day ACU licence, day NHCA membership and one machine&apos;s entry.
            </div>
          </div>
        )}

        {/* Documents */}
        <div style={S.sectionHead}>Regulations Document</div>
        <FormField label="URL (used if no PDF uploaded)" field="regs_url" placeholder="https://…" form={form} onSet={set}
          onBlur={() => maybeTriggerRegsPrompt()} />
        {!isNew && (
          <PdfField calendarId={event.CalendarID} pdfType="regs" label="Regulations"
            hasBlob={pdfState.has_regs_pdf} url={form.regs_url}
            sessionToken={sessionToken} onChanged={refreshPdfState} onUploadSuccess={handleRegsUploaded} />
        )}

        <div style={S.sectionHead}>Entry Form Document</div>
        <FormField label="URL (used if no PDF uploaded)" field="entry_form_url" placeholder="https://…" form={form} onSet={set} />
        {!isNew && (
          <PdfField calendarId={event.CalendarID} pdfType="entry-form" label="Entry Form"
            hasBlob={pdfState.has_entry_form_pdf} url={form.entry_form_url}
            sessionToken={sessionToken} onChanged={refreshPdfState} />
        )}

        <div style={S.sectionHead}>Results URL</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <FormField label="Results URL" field="results_url" placeholder="https://…" form={form} onSet={set} />
          </div>
          {Number(form.Date?.substring(0, 4)) >= 2027 && (
            <button
              type="button"
              style={{ ...S.btnSmall, background: '#1b3d6d', color: 'white', marginBottom: 2 }}
              disabled={!form.Hill?.trim() || !form.Date}
              onClick={() => set('results_url',
                `https://query.nhca.co.uk/results?hill=${encodeURIComponent(form.Hill.trim())}&date=${form.Date}`)}
            >
              Publish
            </button>
          )}
        </div>

        {/* Live timing */}
        <div style={S.sectionHead}>Live Timing</div>
        <div style={S.grid2}>
          <div>
            <label style={S.label}>Timing System</label>
            <select style={S.input} value={form.timing_system ?? ''}
              onChange={e => set('timing_system', e.target.value)}>
              {TIMING_SYSTEMS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <FormField label="Timing URL" field="timing_url" placeholder="https://…" form={form} onSet={set} />
        </div>
        {form.timing_system && (
          <p style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}>
            {TIMING_URL_HINTS[form.timing_system]}
          </p>
        )}

        {isNew && (
          <p style={{ fontSize: '0.82rem', color: '#aaa', marginTop: 4 }}>
            Save the event first, then re-open to upload PDFs.
          </p>
        )}

        <div style={S.modalFooter}>
          <button style={{ ...S.btn, ...S.btnSecondary }} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create Event' : 'Save Changes'}
          </button>
        </div>
      </div>

      {showReadinessDialog && (
        <ReadinessDialog
          event={event}
          form={{ ...form, has_regs_pdf: pdfState.has_regs_pdf }}
          sessionToken={sessionToken}
          secretaryEmails={secretaryEmails}
          onSaved={(updatedEvent, savedFields) => {
            setForm(f => ({ ...f, ...savedFields }));
            onQuietUpdate?.(updatedEvent);
            setShowReadinessDialog(false);
          }}
          onCancel={() => setShowReadinessDialog(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function CalendarEditor() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Arriving via a magic link, ignore any saved session until the link's
  // token is verified: otherwise the events load with the old cookie, and if
  // that session has expired the 401 logs out — wiping the new session the
  // link is creating at the same moment.
  const arrivingWithToken = searchParams.has('token');
  const [sessionToken, setSessionToken] = useState(() => arrivingWithToken ? null : getCookie('cal_editor_session'));
  const [userEmail, setUserEmail] = useState(() => arrivingWithToken ? null : getCookie('cal_editor_email'));
  const [verifying, setVerifying] = useState(arrivingWithToken);
  const [authError, setAuthError] = useState('');

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [lastEventYear, setLastEventYear] = useState(currentYear);
  const [events, setEvents] = useState([]);
  const [hills, setHills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');

  const [modalEvent, setModalEvent] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  // Season confirmation status — drives the "PROVISIONAL" watermark on the
  // public /calendar page. A season with no row yet is provisional by
  // default (see migrations/add_season_status_table.sql), so a new year
  // starts out watermarked with no action needed here; this toggle is only
  // used to confirm it (typically at the AGM) or to revert that if needed.
  const [seasonConfirmed, setSeasonConfirmed] = useState(null); // null = unknown/loading
  const [seasonStatusSaving, setSeasonStatusSaving] = useState(false);

  // Verify magic-link token from URL. Guarded with a ref (not just the
  // effect's own state) because the token is single-use server-side: React
  // StrictMode double-invokes effects in dev, and unlike SecretaryPortal's
  // equivalent (which clears the token from the URL synchronously before
  // the async call), this effect only clears it in the .then() — so without
  // the ref guard, both StrictMode passes fire the verify call with the same
  // token, and the second (correctly-rejected) response overwrites the
  // first's success in the UI even though login actually succeeded.
  const verifyRanRef = useRef(false);
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) return;
    if (verifyRanRef.current) return;
    verifyRanRef.current = true;
    setVerifying(true);
    fetch(`${API}/verify?token=${token}`)
      .then(r => r.json())
      .then(j => {
        if (j.session_token) {
          setCookie('cal_editor_session', j.session_token, 90);
          setCookie('cal_editor_email', j.email, 90);
          setSessionToken(j.session_token);
          setUserEmail(j.email);
          setSearchParams({}, { replace: true });
        } else {
          setAuthError(j.error || 'Login failed – please request a new link.');
        }
      })
      .catch(() => setAuthError('Network error during login.'))
      .finally(() => setVerifying(false));
  }, []);

  // Fetch events when authenticated / year changes
  useEffect(() => {
    if (!sessionToken) return;
    setLoading(true); setListError('');
    fetch(`${API}/events?year=${selectedYear}`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      .then(r => { if (r.status === 401) { handleLogout(); return null; } return r.json(); })
      .then(data => {
        if (!data) return;
        if (Array.isArray(data)) setEvents(data);
        else setListError(data.error || 'Failed to load events');
      })
      .catch(() => setListError('Network error'))
      .finally(() => setLoading(false));
  }, [sessionToken, selectedYear]);

  // The latest year with an event, so the year menu reaches it
  useEffect(() => {
    if (!sessionToken) return;
    fetch(`${API}/events/years`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data?.years?.length) setLastEventYear(Math.max(...data.years)); })
      .catch(() => {});
  }, [sessionToken]);

  // Fetch season confirmation status when authenticated / year changes
  useEffect(() => {
    if (!sessionToken) return;
    setSeasonConfirmed(null);
    fetch(`${API}/season-status?year=${selectedYear}`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSeasonConfirmed(!!data.confirmed); })
      .catch(() => {});
  }, [sessionToken, selectedYear]);

  async function toggleSeasonConfirmed() {
    const next = !seasonConfirmed;
    setSeasonStatusSaving(true);
    try {
      const res = await fetch(`${API}/season-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
        body: JSON.stringify({ year: selectedYear, confirmed: next }),
      });
      const j = await res.json();
      if (res.ok) setSeasonConfirmed(!!j.confirmed);
    } catch { /* leave state as-is; user can retry */ }
    setSeasonStatusSaving(false);
  }

  // Established hill names for the datalist and the new-name warning —
  // fetched after login and again after every save, so a renamed event's
  // old name doesn't linger in the list.
  function loadHills() {
    if (!sessionToken) return;
    fetch(`${API}/hills`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setHills(data); })
      .catch(() => {});
  }
  useEffect(loadHills, [sessionToken]);

  // Fetch previously-used secretary email addresses for autocomplete (once after login)
  const [secretaryEmails, setSecretaryEmails] = useState({});
  useEffect(() => {
    if (!sessionToken) return;
    fetch(`${API}/secretary-emails`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        if (!data || typeof data !== 'object') return;
        // Single-letter values in the "secretary" (display name) column are
        // leftover data-entry noise, not real names — drop them from suggestions.
        if (Array.isArray(data.secretary)) {
          data = { ...data, secretary: data.secretary.filter(v => v && v.trim().length > 1) };
        }
        setSecretaryEmails(data);
      })
      .catch(() => {});
  }, [sessionToken]);

  function handleLogout() {
    if (sessionToken) {
      fetch(`${API}/logout`, { method: 'POST', headers: { Authorization: `Bearer ${sessionToken}` } }).catch(() => {});
    }
    deleteCookie('cal_editor_session');
    deleteCookie('cal_editor_email');
    setSessionToken(null);
    setUserEmail(null);
  }

  function handleSaved(savedEvent) {
    setEvents(prev => {
      const idx = prev.findIndex(e => e.CalendarID === savedEvent.CalendarID);
      const next = idx >= 0
        ? prev.map((e, i) => (i === idx ? savedEvent : e))
        : [...prev, savedEvent];
      return next.sort((a, b) => (a.Date || '').localeCompare(b.Date || ''));
    });
    setModalEvent(undefined);
    loadHills();
  }

  // Updates the table row in place without closing the edit modal — used by
  // PDF uploads and the Entry List readiness dialog, both of which persist
  // immediately rather than waiting for "Save Changes".
  function handleQuietUpdate(updatedEvent) {
    setEvents(prev => prev.map(e => (e.CalendarID === updatedEvent.CalendarID ? { ...e, ...updatedEvent } : e)));
  }

  async function handleDelete(calendarId) {
    setDeleteError('');
    try {
      const res = await fetch(`${API}/events/${calendarId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const j = await res.json();
      if (!res.ok) setDeleteError(j.error || 'Delete failed');
      else { setEvents(prev => prev.filter(e => e.CalendarID !== calendarId)); setDeleteTarget(null); }
    } catch { setDeleteError('Network error'); }
  }

  // ------------------------------------------------------------------
  // Render states
  // ------------------------------------------------------------------
  if (verifying) {
    return <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p>Logging in…</p></div>;
  }
  if (authError) {
    return (
      <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <div style={S.error}>{authError}</div>
          <button style={{ ...S.btn, ...S.btnPrimary, marginTop: 12 }} onClick={() => setAuthError('')}>Try again</button>
        </div>
      </div>
    );
  }
  if (!sessionToken) return <LoginScreen />;

  const yearOptions = [];
  for (let y = currentYear - 1; y <= Math.max(currentYear + 2, lastEventYear); y++) yearOptions.push(y);

  const classBadges = (ev) => [
    [ev.open, 'Open', '#3498db'],
    [ev.roadlegal, 'RL', '#27ae60'],
    [ev.forgotten_era, 'FE', '#8e44ad'],
    [ev.vc, 'VC', '#e67e22'],
    [ev.restricted, 'Restricted', '#7f8c8d'],
    [ev.giag, 'GIAG', '#1abc9c'],
    [ev.under16 === 'Yes', 'U16: Yes', '#e91e63'],
    [ev.under16 === 'Ask', 'U16: Ask', '#c0392b'],
  ].filter(([on]) => on).map(([, lbl, col]) => (
    <span key={lbl} style={{ ...S.badge, background: col + '22', color: col }}>{lbl}</span>
  ));

  const docLinks = (ev) => {
    const regsHref = ev.has_regs_pdf ? `${API}/events/${ev.CalendarID}/pdf/regs` : ev.regs_url || null;
    const entryHref = ev.has_entry_form_pdf ? `${API}/events/${ev.CalendarID}/pdf/entry-form` : ev.entry_form_url || null;
    return (
      <>
        {regsHref && <a href={regsHref} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-block', marginRight: 8, fontSize: '0.8rem', color: '#1b3d6d' }}>
          Regs{ev.has_regs_pdf ? ' 📎' : ''}
        </a>}
        {entryHref && <a href={entryHref} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-block', marginRight: 8, fontSize: '0.8rem', color: '#1b3d6d' }}>
          Entry{ev.has_entry_form_pdf ? ' 📎' : ''}
        </a>}
        {ev.results_url && <a href={ev.results_url} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: '0.8rem', color: '#27ae60' }}>
          Results
        </a>}
      </>
    );
  };

  const entryListCell = (ev) => {
    if (!isOnlineEntryEra(ev.Date)) {
      return <span style={{ fontSize: '0.8rem', color: '#ccc' }} title="Online entry form starts from the 2027 season">—</span>;
    }
    if (ev.external_entries) {
      return <span style={{ fontSize: '0.8rem', color: '#888' }} title="Entries are handled by the organiser, not through this website">Organiser</span>;
    }
    const missing = missingEntryListFields(ev);
    // No entries while the season is provisional: the public calendar hides
    // the Enter link and the form itself stays closed until it's confirmed.
    if (missing.length === 0 && !seasonConfirmed) {
      return (
        <span
          title="Not yet available: the season is still provisional. Online entry opens when you click Confirm season above."
          style={{ ...S.btnSmall, background: '#e0e4ea', color: '#999', display: 'inline-block', cursor: 'not-allowed' }}>
          Entry List
        </span>
      );
    }
    if (missing.length === 0) {
      return (
        <a href={`/enter/${ev.CalendarID}`} target="_blank" rel="noopener noreferrer"
          style={{ ...S.btnSmall, background: '#27ae60', color: 'white', display: 'inline-block', textDecoration: 'none' }}>
          Entry List ✓
        </a>
      );
    }
    return (
      <span
        title={`Not yet available. Missing: ${missing.map(m => m.label).join(', ')}`}
        style={{ ...S.btnSmall, background: '#e0e4ea', color: '#999', display: 'inline-block', cursor: 'not-allowed' }}>
        Entry List
      </span>
    );
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.headerTitle}>📅 NHCA Calendar Editor</h1>
          <div style={S.headerMeta}>Signed in as {userEmail}</div>
        </div>
        <button style={{ ...S.btn, background: 'rgba(255,255,255,0.15)', color: 'white' }} onClick={handleLogout}>
          Sign out
        </button>
      </div>

      <div style={S.wrap}>
        {/* Controls bar */}
        <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Year:</label>
            <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
              style={{ ...S.input, width: 100 }}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => setModalEvent(null)}>
            + New Event
          </button>
          {/* Status and action are shown separately: a single toggle
              labelled with its state left people unsure which way a click
              would go. */}
          {seasonConfirmed !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span
                title={seasonConfirmed
                  ? 'The public calendar shows this season with no "PROVISIONAL" watermark.'
                  : 'The public calendar shows this season with a "PROVISIONAL" watermark.'}
                style={{
                  fontSize: '0.88rem', fontWeight: 600, padding: '0.35rem 0.6rem', borderRadius: 4,
                  background: seasonConfirmed ? '#e8f5ea' : '#fff4e5',
                  color: seasonConfirmed ? '#27ae60' : '#c0680a',
                }}
              >
                {seasonConfirmed ? `✓ ${selectedYear} season is confirmed` : `⚠ ${selectedYear} season is provisional`}
              </span>
              <button
                onClick={toggleSeasonConfirmed}
                disabled={seasonStatusSaving}
                title={seasonConfirmed
                  ? 'Put the "PROVISIONAL" watermark back on the public calendar for this season. You can confirm it again at any time.'
                  : 'Remove the "PROVISIONAL" watermark from the public calendar for this season, e.g. once the AGM approves the fixture list. You can undo this at any time.'}
                style={{
                  ...S.btn,
                  ...(seasonConfirmed
                    ? { background: 'white', color: '#c0680a', border: '1.5px solid #fb6107' }
                    : { background: '#27ae60', color: 'white', border: '1.5px solid #27ae60' }),
                  opacity: seasonStatusSaving ? 0.6 : 1,
                }}
              >
                {seasonStatusSaving ? '…' : seasonConfirmed ? 'Mark as provisional' : 'Confirm season'}
              </button>
            </div>
          )}
          <span style={{ marginLeft: 'auto', color: '#888', fontSize: '0.85rem' }}>
            {loading ? 'Loading…' : `${events.length} event${events.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* Events table */}
        <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
          {listError && <div style={{ ...S.error, margin: '1rem' }}>{listError}</div>}
          {deleteError && <div style={{ ...S.error, margin: '1rem' }}>{deleteError}</div>}
          {!loading && events.length === 0 && !listError && (
            <p style={{ padding: '2rem', textAlign: 'center', color: '#bbb' }}>No events for {selectedYear}.</p>
          )}
          {events.length > 0 && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>ID</th>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Hill</th>
                  <th style={S.th}>Event Name</th>
                  <th style={S.th}>Classes / Flags</th>
                  <th style={S.th}>Docs</th>
                  <th style={S.th}>Entry List</th>
                  <th style={S.th}>Secretary</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => {
                  // 2027+ events still missing something online entry needs
                  // (the season's provisional state is shown separately above).
                  const missing = usesOnlineEntry(ev) ? missingEntryListFields(ev) : [];
                  const missingText = missing.map(m => m.label).join(', ');
                  return (
                  <tr key={ev.CalendarID}
                    title={missing.length ? `Online entry can't open until these are set: ${missingText}` : undefined}
                    style={{ background: deleteTarget === ev.CalendarID ? '#fff5f5' : missing.length ? '#fff8ec' : undefined }}>
                    <td style={{ ...S.td, color: '#aaa', fontSize: '0.8rem', borderLeft: missing.length ? '4px solid #fb6107' : '4px solid transparent' }}>{ev.CalendarID}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{ev.Date?.substring(0, 10)}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>
                      {ev.Hill}
                      {missing.length > 0 && (
                        <div style={{ fontWeight: 400, fontSize: '0.78rem', color: '#c0680a', marginTop: 2 }}>
                          ⚠ Missing: {missingText}
                        </div>
                      )}
                    </td>
                    <td style={S.td}>{ev.eventname}</td>
                    <td style={S.td}>{classBadges(ev)}</td>
                    <td style={S.td}>{docLinks(ev)}</td>
                    <td style={S.td}>{entryListCell(ev)}</td>
                    <td style={{ ...S.td, fontSize: '0.85rem' }}>{ev.secretary || ''}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      {deleteTarget === ev.CalendarID ? (
                        <>
                          <span style={{ fontSize: '0.82rem', marginRight: 6, color: '#c0392b' }}>Delete?</span>
                          <button style={{ ...S.btnSmall, ...S.btnDanger, marginRight: 4 }} onClick={() => handleDelete(ev.CalendarID)}>Yes</button>
                          <button style={{ ...S.btnSmall, background: '#e0e4ea', color: '#333' }} onClick={() => setDeleteTarget(null)}>No</button>
                        </>
                      ) : (
                        <>
                          <button style={{ ...S.btnSmall, background: '#1b3d6d', color: 'white', marginRight: 4 }}
                            onClick={() => setModalEvent(ev)}>Edit</button>
                          <button style={{ ...S.btnSmall, ...S.btnDanger }}
                            onClick={() => { setDeleteTarget(ev.CalendarID); setDeleteError(''); }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Rounds per class for the selected season */}
        {events.length > 0 && (
          <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{selectedYear} rounds:</span>
            {[
              ['open', 'Open', '#3498db'],
              ['forgotten_era', 'FE', '#8e44ad'],
              ['roadlegal', 'RL', '#27ae60'],
              ['vc', 'VC', '#e67e22'],
            ].map(([key, lbl, col]) => (
              <span key={key} style={{ ...S.badge, background: col + '22', color: col, fontSize: '0.85rem' }}>
                {lbl}: {events.filter(ev => ev[key]).length}
              </span>
            ))}
          </div>
        )}
      </div>

      {modalEvent !== undefined && (
        <EventModal
          event={modalEvent}
          sessionToken={sessionToken}
          hills={hills}
          secretaryEmails={secretaryEmails}
          onSave={handleSaved}
          onClose={() => setModalEvent(undefined)}
          onQuietUpdate={handleQuietUpdate}
          defaultYear={selectedYear}
        />
      )}
    </div>
  );
}
